import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { inr } from "@/lib/api";
import { toast } from "sonner";

export default function ProductCard({ p }) {
  const { user, refreshWishlist, refreshCart, wishlist } = useAuth();
  const isWished = (wishlist || []).some(w => w.product_id === p.product_id);
  const discount = p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0;

  const toggleWish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Please login to use wishlist"); return; }
    try { await api.post("/wishlist/toggle", { product_id: p.product_id }); await refreshWishlist(); }
    catch { toast.error("Could not update wishlist"); }
  };

  const addCart = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Please login to add to cart"); return; }
    try {
      await api.post("/cart/add", { product_id: p.product_id, quantity: 1, size: p.sizes?.[0] || null, color: p.colors?.[0] || null });
      await refreshCart();
      toast.success("Added to cart");
    } catch { toast.error("Could not add to cart"); }
  };

  return (
    <Link to={`/product/${p.product_id}`} data-testid={`product-card-${p.product_id}`} className="product-card group relative flex flex-col bg-white rounded-2xl border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
        <img src={p.images?.[0]} alt={p.title} className="product-card-img w-full h-full object-cover" />
        {discount > 0 && (
          <span className="absolute top-3 left-3 bg-[#FF4500] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">{discount}% OFF</span>
        )}
        <button data-testid={`wishlist-btn-${p.product_id}`} onClick={toggleWish} className="absolute top-3 right-3 grid place-items-center w-9 h-9 rounded-full bg-white/95 backdrop-blur shadow-md hover:bg-white">
          <Heart size={16} className={isWished ? "fill-[#FF4500] text-[#FF4500]" : "text-slate-700"} strokeWidth={1.7} />
        </button>
        <button data-testid={`add-cart-btn-${p.product_id}`} onClick={addCart} className="absolute bottom-3 right-3 grid place-items-center w-10 h-10 rounded-full bg-[#004BCA] text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
          <ShoppingBag size={16} strokeWidth={2} />
        </button>
      </div>
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{p.brand}</div>
        <div className="text-sm font-medium text-slate-900 truncate mt-1">{p.title}</div>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-base font-semibold text-[#004BCA]">{inr(p.price)}</span>
          {p.mrp > p.price && <span className="text-xs line-through text-slate-400">{inr(p.mrp)}</span>}
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded">{p.rating?.toFixed?.(1) || p.rating} ★</span>
          <span className="text-[11px] text-slate-500">In stock</span>
        </div>
      </div>
    </Link>
  );
}
